/**
 * The annotations block: reference lines dropped on a plot at the categories where something
 * happened — a price change, a release, an outage. It declares no display type of its own. It is
 * carried by the types whose renderer draws it, named in `carriedBy` below.
 */

const annotations = {
  /* No names: a block, not a renderer. */
  names: [],
  block: 'annotations',
  /* Which display types read this block. Not `sharedBlock` — that says every type may carry it, and
     only `columns` can. A line dropped down a plot means nothing on a pie, and a pareto ranks its
     rows into synthetic categories that no source column can name. Opening it to another type is
     one id here plus that type's renderer drawing it, the same discipline `events` follows. */
  carriedBy: ['bar', 'line', 'combo'],
  fields: {
    /* A column rather than a list of dates typed into the config, for the same reason a quadrant's
       dividers are columns (scatter.js): a date in the config has to be matched back onto a
       category the axis actually drew — date parsing, timezone, bucket grain, near-miss snapping —
       while a marked ROW marks its own category by construction. It doubles as the label because a
       column carrying the event's name carries both facts in one place. Cleared rather than
       required, because clearing it is how the lines come off. */
    labelKey: { kind: 'string', clearable: true, label: 'Event label' }
  },
  /* Advanced mode only, where the whole config is returned fresh each resolution: code that wants
     no lines omits the block, so a block arriving with no column is a typo rather than an intent. */
  validate: ({ block }) => (block.labelKey ? '' : 'annotations needs labelKey, the column that names each event — or leave the block out entirely, which is how a config carries no lines.'),
  stateSummary: ({ block }) => (block.labelKey ? `annotations: event lines from ${block.labelKey}` : ''),
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: [],
    description: "Event lines (bar, line and combo charts): dashed vertical lines marking the categories where something happened — a launch, a price change, an outage — so the reader sees the cause beside the effect. The events are a COLUMN on the same rows the panel already plots, never a list of dates here: a row whose column carries text gets a line labelled with it, every other row gets nothing. Bring the column in with the panel's own SQL — LEFT JOIN the table the events live in, or `CASE WHEN day = '2026-07-22' THEN 'Pricing change' END AS event` for a date the user named — which is also what keeps the lines correct after a refresh. An event on a date the chart does not plot draws no line, so emit that row from SQL if the story needs it.",
    properties: {
      labelKey: {
        type: ['string', 'null'],
        description: 'Column naming what happened, read from the rows the panel plots (e.g. "event"). Rows where it is empty or null are the normal case and draw nothing. The text is shown when the reader hovers that category, so write it as a short human phrase ("Pricing change"), not an id. Pass "" to take the lines off the panel.'
      }
    }
  }
};

export default annotations;
